/**
 * DeleteModal Component
 *
 * A confirmation dialog for permanently deleting items.
 * Warns users about the irreversible nature of deletion.
 */

import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { Item, ItemType } from '@/types';

// ============================================
// TYPES
// ============================================

export interface DeleteModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Item to delete */
  item: Item | null;
  /** Close handler */
  onClose: () => void;
  /** Confirm delete handler */
  onConfirm: (item: Item) => Promise<void>;
}

// ============================================
// HELPERS
// ============================================

const typeLabels: Record<ItemType, string> = {
  link: 'link',
  podcast: 'podcast',
  note: 'note',
  idea: 'idea',
  file: 'file',
  task: 'task',
};

function getDeleteWarning(item: Item): string {
  switch (item.type) {
    case 'file':
      return 'This will also delete the file from Google Drive.';
    case 'task':
      return 'All subtasks will also be deleted.';
    case 'note':
    case 'idea':
      return 'All content within this item will be permanently lost.';
    default:
      return '';
  }
}

// ============================================
// MAIN COMPONENT
// ============================================

export function DeleteModal({
  isOpen,
  item,
  onClose,
  onConfirm,
}: DeleteModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!item) return;

    setIsLoading(true);
    setError(null);

    try {
      await onConfirm(item);
      onClose();
    } catch (err) {
      console.error('Failed to delete item:', err);
      setError('Failed to delete item. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError(null);
      onClose();
    }
  };

  if (!item) return null;

  const typeLabel = typeLabels[item.type];
  const additionalWarning = getDeleteWarning(item);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 p-2 rounded-full bg-red-100">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <DialogTitle>Delete {typeLabel}?</DialogTitle>
              <DialogDescription>
                This action cannot be undone.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {/* Item info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="font-medium text-gray-900 truncate">{item.title}</p>
            {item.description && (
              <p className="text-sm text-gray-500 truncate mt-1">{item.description}</p>
            )}
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
              <span className="capitalize">{item.type}</span>
              {item.tags.length > 0 && (
                <>
                  <span>&middot;</span>
                  <span>{item.tags.length} tag{item.tags.length !== 1 ? 's' : ''}</span>
                </>
              )}
            </div>
          </div>

          {/* Additional warning for specific types */}
          {additionalWarning && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{additionalWarning}</p>
            </div>
          )}

          {/* Warning */}
          <p className="text-sm text-gray-600">
            Are you sure you want to delete "{item.title}"? This will permanently
            remove it from your personal workspace and cannot be recovered.
          </p>

          {/* Error message */}
          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3">
              {error}
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
